import { readFile } from 'fs/promises';
import { BaseMetadataCollector } from './base.js';
import { normalizePath } from '../utils/index.js';
import type {
  PlaywrightMetadata,
  ProjectConfig,
  ProjectUseOptions,
  RunSkeleton,
  SkeletonSuite,
  SkeletonTest,
} from './types.js';

export interface PlaywrightFullProject {
  name?: string;
  testDir?: string;
  timeout?: number;
  retries?: number;
  repeatEach?: number;
  dependencies?: string[];
  grep?: RegExp | RegExp[];
  use?: {
    browserName?: 'chromium' | 'firefox' | 'webkit';
    defaultBrowserType?: 'chromium' | 'firefox' | 'webkit';
    channel?: string;
    headless?: boolean;
    viewport?: { width: number; height: number } | null;
    baseURL?: string;
    trace?: string | { mode: string };
    screenshot?: string | { mode: string };
    video?: string | { mode: string };
    isMobile?: boolean;
    locale?: string;
    [key: string]: unknown;
  };
}

// testDir/timeout/retries live on FullProject, not FullConfig.
export interface PlaywrightFullConfig {
  fullyParallel?: boolean;
  workers?: number;
  shard?: { current: number; total: number } | null;
  projects?: PlaywrightFullProject[];
  version?: string;
  configFile?: string;
  forbidOnly?: boolean;
  globalTimeout?: number;
  grep?: RegExp | RegExp[];
  maxFailures?: number;
  metadata?: Record<string, unknown>;
  reportSlowTests?: { max: number; threshold: number } | null;
  rootDir?: string;
  tags?: string[];
  webServer?: unknown;
}

export interface PlaywrightSuite {
  title: string;
  type: 'root' | 'project' | 'file' | 'describe';
  location?: { file: string; line: number; column: number };
  suites: PlaywrightSuite[];
  tests: PlaywrightTestCase[];
  allTests(): PlaywrightTestCase[];
}

export interface PlaywrightTestCase {
  id: string;
  title: string;
  location: { file: string; line: number; column: number };
  tags: string[];
  expectedStatus: 'passed' | 'failed' | 'timedOut' | 'skipped' | 'interrupted';
  annotations: Array<{ type: string; description?: string }>;
}

export interface PlaywrightCollectorOptions {
  timeout?: number;
  config?: PlaywrightFullConfig;
  suite?: PlaywrightSuite;
  packageJsonPath?: string;
}

export class PlaywrightMetadataCollector extends BaseMetadataCollector<PlaywrightMetadata> {
  private options: Required<Omit<PlaywrightCollectorOptions, 'config' | 'suite' | 'packageJsonPath'>> &
    Pick<PlaywrightCollectorOptions, 'config' | 'suite' | 'packageJsonPath'>;

  constructor(options: PlaywrightCollectorOptions = {}) {
    super('playwright');
    this.options = {
      timeout: options.timeout || 3000,
      config: options.config,
      suite: options.suite,
      packageJsonPath: options.packageJsonPath,
    };
  }

  protected async collectMetadata(): Promise<PlaywrightMetadata> {
    const metadata: PlaywrightMetadata = {};

    const version = await this.getPlaywrightVersion();
    if (version) metadata.version = version;

    if (this.options.config) {
      Object.assign(metadata, this.extractConfigMetadata(this.options.config));
    }

    return metadata;
  }

  protected getEmptyMetadata(): PlaywrightMetadata {
    return {};
  }

  private async getPlaywrightVersion(): Promise<string | undefined> {
    try {
      const packageJsonPath = this.options.packageJsonPath || this.resolvePlaywrightPackageJson();
      if (!packageJsonPath) return undefined;

      const content = await this.withTimeout(
        readFile(packageJsonPath, 'utf-8'),
        this.options.timeout,
        'Playwright package.json read'
      );
      const packageJson = this.safeJsonParse<{ version?: string }>(content, {});
      return this.isNonEmptyString(packageJson.version) ? packageJson.version : undefined;
    } catch (error) {
      console.warn(
        '⚠️  TestDino: Failed to read Playwright version:',
        error instanceof Error ? error.message : String(error)
      );
      return undefined;
    }
  }

  private resolvePlaywrightPackageJson(): string | undefined {
    try {
      return require.resolve('@playwright/test/package.json');
    } catch {
      return undefined;
    }
  }

  private extractConfigMetadata(config: PlaywrightFullConfig): Partial<PlaywrightMetadata> {
    const metadata: Partial<PlaywrightMetadata> = {};

    if (this.isNonEmptyString(config.configFile)) metadata.configFile = config.configFile;
    if (typeof config.forbidOnly === 'boolean') metadata.forbidOnly = config.forbidOnly;

    if (typeof config.fullyParallel === 'boolean') {
      metadata.fullyParallel = config.fullyParallel;
      metadata.parallel = config.fullyParallel;
    }

    if (typeof config.globalTimeout === 'number') metadata.globalTimeout = config.globalTimeout;

    if (config.grep) {
      const patterns = Array.isArray(config.grep) ? config.grep : [config.grep];
      metadata.grep = patterns.map((p) => p.source);
    }

    if (typeof config.maxFailures === 'number') metadata.maxFailures = config.maxFailures;
    if (config.metadata && typeof config.metadata === 'object') metadata.metadata = config.metadata;
    if (typeof config.workers === 'number') metadata.workers = config.workers;

    if (Array.isArray(config.projects) && config.projects.length > 0) {
      const projectConfigs = config.projects
        .filter((project) => this.isNonEmptyString(project.name))
        .map((project) => this.extractProjectConfig(project));

      if (projectConfigs.length > 0) {
        metadata.projects = projectConfigs;
        const browsers = this.extractBrowsersFromProjects(config.projects);
        if (browsers.length > 0) metadata.browsers = browsers;
      }
    }

    if (config.reportSlowTests && typeof config.reportSlowTests === 'object') {
      metadata.reportSlowTests = {
        max: config.reportSlowTests.max,
        threshold: config.reportSlowTests.threshold,
      };
    }

    if (this.isNonEmptyString(config.rootDir)) metadata.rootDir = config.rootDir;

    if (config.shard && typeof config.shard.current === 'number' && typeof config.shard.total === 'number') {
      metadata.shard = { current: config.shard.current, total: config.shard.total };
    }

    if (Array.isArray(config.tags) && config.tags.length > 0) metadata.tags = config.tags;
    if (config.webServer && typeof config.webServer === 'object') {
      metadata.webServer = config.webServer as Record<string, unknown>;
    }

    return metadata;
  }

  private extractProjectConfig(project: PlaywrightFullProject): ProjectConfig {
    const config: ProjectConfig = { name: project.name || '' };

    if (this.isNonEmptyString(project.testDir)) config.testDir = project.testDir;
    if (typeof project.timeout === 'number') config.timeout = project.timeout;
    if (typeof project.retries === 'number') config.retries = project.retries;
    if (typeof project.repeatEach === 'number' && project.repeatEach > 1) config.repeatEach = project.repeatEach;

    if (Array.isArray(project.dependencies) && project.dependencies.length > 0) {
      config.dependencies = project.dependencies;
    }

    if (project.grep) {
      const patterns = Array.isArray(project.grep) ? project.grep : [project.grep];
      const grepStrings = patterns.map((p) => p.source);
      if (grepStrings.length > 0) config.grep = grepStrings;
    }

    if (project.use) {
      const useOptions = this.extractUseOptions(project.use);
      if (Object.keys(useOptions).length > 0) config.use = useOptions;
    }

    return config;
  }

  private extractUseOptions(use: PlaywrightFullProject['use']): ProjectUseOptions {
    const options: ProjectUseOptions = {};
    if (!use) return options;

    if (this.isNonEmptyString(use.channel)) options.channel = use.channel;

    const browserName = this.resolveBrowserName(use.browserName, use.defaultBrowserType, use.channel);
    if (browserName) options.browserName = browserName;

    if (typeof use.headless === 'boolean') options.headless = use.headless;

    if (use.viewport && typeof use.viewport === 'object') {
      options.viewport = { width: use.viewport.width, height: use.viewport.height };
    } else if (use.viewport === null) {
      options.viewport = null;
    }

    if (this.isNonEmptyString(use.baseURL)) options.baseURL = use.baseURL;

    const trace = this.normalizeArtifactMode(use.trace);
    if (trace) options.trace = trace;

    const screenshot = this.normalizeArtifactMode(use.screenshot);
    if (screenshot) options.screenshot = screenshot;

    const video = this.normalizeArtifactMode(use.video);
    if (video) options.video = video;

    if (typeof use.isMobile === 'boolean') options.isMobile = use.isMobile;
    if (this.isNonEmptyString(use.locale)) options.locale = use.locale;

    return options;
  }

  // trace/screenshot/video can be string or { mode: string }.
  private normalizeArtifactMode(value: unknown): string | undefined {
    if (!value) return undefined;
    if (typeof value === 'string' && value !== 'off') return value;
    if (typeof value === 'object' && value !== null && 'mode' in value) {
      const mode = (value as { mode: string }).mode;
      if (mode && mode !== 'off') return mode;
    }
    return undefined;
  }

  // Priority: explicit browserName > defaultBrowserType (device presets) > channel inference.
  private resolveBrowserName(
    browserName: string | undefined,
    defaultBrowserType: string | undefined,
    channel: string | undefined
  ): 'chromium' | 'firefox' | 'webkit' | undefined {
    const validBrowsers = ['chromium', 'firefox', 'webkit'];

    if (browserName && validBrowsers.includes(browserName)) {
      return browserName as 'chromium' | 'firefox' | 'webkit';
    }
    if (defaultBrowserType && validBrowsers.includes(defaultBrowserType)) {
      return defaultBrowserType as 'chromium' | 'firefox' | 'webkit';
    }
    if (channel) {
      const chromiumChannels = [
        'chrome',
        'chrome-beta',
        'chrome-dev',
        'chrome-canary',
        'msedge',
        'msedge-beta',
        'msedge-dev',
        'msedge-canary',
      ];
      if (chromiumChannels.includes(channel)) return 'chromium';
    }
    return undefined;
  }

  private extractBrowsersFromProjects(projects: PlaywrightFullProject[]): string[] {
    const browsers = new Set<string>();
    for (const project of projects) {
      const browserName = this.resolveBrowserName(
        project.use?.browserName,
        project.use?.defaultBrowserType,
        project.use?.channel
      );
      if (browserName) browsers.add(browserName);
    }
    return Array.from(browsers);
  }

  buildSkeleton(suite: PlaywrightSuite): RunSkeleton {
    return {
      totalTests: suite.allTests().length,
      suites: this.buildSuiteTree(suite),
    };
  }

  private buildSuiteTree(suite: PlaywrightSuite): SkeletonSuite[] {
    const suites: SkeletonSuite[] = [];

    for (const childSuite of suite.suites) {
      const skeletonSuite: SkeletonSuite = {
        title: childSuite.title,
        type: childSuite.type === 'file' ? 'file' : 'describe',
        tests: childSuite.tests.map((test) => this.buildSkeletonTest(test)),
      };

      if (childSuite.type === 'file' && childSuite.location) {
        skeletonSuite.file = normalizePath(childSuite.location.file, this.options.config?.rootDir);
      }

      if (childSuite.location) {
        skeletonSuite.location = {
          file: normalizePath(childSuite.location.file, this.options.config?.rootDir),
          line: childSuite.location.line,
          column: childSuite.location.column,
        };
      }

      if (childSuite.suites.length > 0) {
        skeletonSuite.suites = this.buildSuiteTree(childSuite);
      }

      suites.push(skeletonSuite);
    }

    return suites;
  }

  private buildSkeletonTest(test: PlaywrightTestCase): SkeletonTest {
    const skeletonTest: SkeletonTest = {
      testId: test.id,
      title: test.title,
      location: {
        file: normalizePath(test.location.file, this.options.config?.rootDir),
        line: test.location.line,
        column: test.location.column,
      },
    };

    if (test.tags && test.tags.length > 0) skeletonTest.tags = test.tags;
    if (test.expectedStatus) skeletonTest.expectedStatus = test.expectedStatus;
    if (test.annotations && test.annotations.length > 0) {
      skeletonTest.annotations = test.annotations.map((ann) => ({
        type: ann.type,
        description: ann.description,
      }));
    }

    return skeletonTest;
  }
}
